import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './effects.css';
import './tutorial.css';
import App from './App';
import TrainingMode from './TrainingMode';
import Login from './Login';
import Menu from './Menu';
import OpponentSelection from './OpponentSelection';
import MartialTournament from './MartialTournament';
import DailySpin from './DailySpin';
import { PoseProvider } from './PoseContext';
import { scheduleInactivityNotifications } from './notifications/notificationService';
import { showInterstitialAd } from './ads/adService';
import { MartialStyles } from './MartialOpponentAI';


// Caché simple para evitar múltiples peticiones
const profileCache = {
  data: null,
  timestamp: 0,
  ttl: 5000 // 5 segundos
};

const fetchProfileWithCache = async (token) => {
  const now = Date.now();
  if (profileCache.data && (now - profileCache.timestamp) < profileCache.ttl) {
    return profileCache.data;
  }
  
  const res = await fetch(`${process.env.REACT_APP_API_URL}/profile`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  const data = await res.json();
  
  profileCache.data = data;
  profileCache.timestamp = now;
  return data;
};

function Root() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [currentScreen, setCurrentScreen] = useState('login'); // 'login', 'menu', 'opponent-selection', 'game', 'training'
  const [selectedOpponent, setSelectedOpponent] = useState(null);
  const [activeChallengeId, setActiveChallengeId] = useState(null);
  const [showDailySpin, setShowDailySpin] = useState(false);
  const [martialFightIndex, setMartialFightIndex] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchProfileWithCache(token)
      .then(data => {
        if (data.nombre) {
          localStorage.setItem('userName', data.nombre);
          setUser({ ...data, name: data.nombre });
          setIsAuthenticated(true);
          setCurrentScreen('menu');
          scheduleInactivityNotifications();
          
          // Verificar ruleta diaria también al auto-login
          setTimeout(async () => {
            try {
              const res = await fetch(`${process.env.REACT_APP_API_URL}/profile/daily-spin`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              const spinData = await res.json();
              if (spinData.canSpin) setShowDailySpin(true);
            } catch (e) { /* silencioso */ }
          }, 500);
        }
      })
      .catch(() => {
        const userName = localStorage.getItem('userName');
        if (userName) {
          setUser({ name: userName });
          setIsAuthenticated(true);
          setCurrentScreen('menu');
        }
      });
    }
  }, []);

  const handleLogin = (data) => {
    const userName = data.nombre || data.name || 'Guerrero';
    localStorage.setItem('userName', userName);
    localStorage.setItem('userData', JSON.stringify({ ...data, genero: data.genero }));
    setIsAuthenticated(true);
    setUser({ name: userName, ...data });
    setCurrentScreen('menu');
    scheduleInactivityNotifications();
    
    // Verificar si puede girar la ruleta diaria
    setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${process.env.REACT_APP_API_URL}/profile/daily-spin`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const spinData = await res.json();
        if (spinData.canSpin) setShowDailySpin(true);
      } catch (e) { /* silencioso */ }
    }, 500);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('userName');
    localStorage.removeItem('userAvatar');
    setIsAuthenticated(false);
    setUser(null);
    setCurrentScreen('login');
  };

  const handleStartTraining = () => {
    setCurrentScreen('training');
  };

  const handleStartCombatIA = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const data = await fetchProfileWithCache(token);
        if (data.nombre) {
          setUser({ ...data, name: data.nombre });
        }
      } catch (error) {
        console.error('Error al recargar perfil:', error);
      }
    }
    setCurrentScreen('opponent-selection');
  };

  const handleStartCombat = async (opponent) => {
    // Mostrar intersticial antes de iniciar combate si está pendiente o toca
    gameCountRef.current += 1;
    sessionStorage.setItem('gameCount', gameCountRef.current.toString());
    if (gameCountRef.current % 3 === 0 || adPendingRef.current) {
      try {
        await showInterstitialAd();
        adPendingRef.current = false;
        sessionStorage.removeItem('adPending');
      } catch (e) {
        console.log('Ad intersticial no disponible:', e.message);
        adPendingRef.current = true;
        sessionStorage.setItem('adPending', 'true');
      }
    }
    setSelectedOpponent(opponent);
    setActiveChallengeId(null);
    setCurrentScreen('game');
  };

  // Desafío: mapeo de oponente_ia id a datos completos del oponente
  const opponentMap = {
    novice: { id: 'novice', name: 'NOVICIO', belt: 'Blanco', energy: 0, img: '/assets/versus/novicio.png' },
    boosabon: { id: 'boosabon', name: 'BOOSABON', belt: 'Negro 1° Dan', energy: 0, img: '/assets/versus/boosabon.png' },
    sabon: { id: 'sabon', name: 'SABON', belt: 'Negro 2° Dan', energy: 0, img: '/assets/versus/sabon.png' },
    'sabon-nim': { id: 'sabon-nim', name: 'SABON NIM', belt: 'Negro 4° Dan', energy: 0, img: '/assets/versus/sabonnim.png' },
    champion: { id: 'champion', name: 'CAMPEÓN', belt: 'Negro 3° Dan', energy: 0, img: '/assets/versus/woldchapion.png' },
    master: { id: 'master', name: 'MASTER', belt: 'Negro 7° Dan', energy: 0, img: '/assets/versus/master.png' },
    grandmaster: { id: 'grandmaster', name: 'GRAN MASTER', belt: 'Negro 9° Dan', energy: 0, img: '/assets/versus/grandmaster.png' }
  };

  // Torneo Marcial: exponer función global para iniciar pelea
  useEffect(() => {
    window._startMartialFight = async (index) => {
      // Mostrar intersticial antes de iniciar si toca
      gameCountRef.current += 1;
      sessionStorage.setItem('gameCount', gameCountRef.current.toString());
      if (gameCountRef.current % 3 === 0 || adPendingRef.current) {
        try {
          await showInterstitialAd();
          adPendingRef.current = false;
          sessionStorage.removeItem('adPending');
        } catch (e) {
          console.log('Ad intersticial no disponible:', e.message);
          adPendingRef.current = true;
          sessionStorage.setItem('adPending', 'true');
        }
      }
      setMartialFightIndex(index);
      const style = MartialStyles[index];
      setSelectedOpponent({
        id: `martial_${style.id}`,
        name: style.name,
        belt: 'Torneo Marcial',
        energy: 0,
        img: `/torneomartiarl/${style.folder}/presentacion.png`,
        martialIndex: index
      });
      setActiveChallengeId(null);
      setCurrentScreen('game');
    };
    return () => { delete window._startMartialFight; };
  }, []);

  const handleStartChallenge = (challengeId, oponenteIa) => {
    const opponent = opponentMap[oponenteIa];
    if (!opponent) return;
    setSelectedOpponent(opponent);
    setActiveChallengeId(challengeId);
    setCurrentScreen('game');
  };

  const gameCountRef = useRef(parseInt(sessionStorage.getItem('gameCount') || '0'));
  const adPendingRef = useRef(sessionStorage.getItem('adPending') === 'true');

  const handleBackToMenu = async () => {

    // Si venimos de torneo marcial, volver al torneo
    if (martialFightIndex !== null) {
      setMartialFightIndex(null);
      setCurrentScreen('martial-tournament');
      profileCache.timestamp = 0;
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const data = await fetchProfileWithCache(token);
          if (data.nombre) setUser({ ...data, name: data.nombre });
        } catch (e) {}
      }
      return;
    }

    profileCache.timestamp = 0;
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const data = await fetchProfileWithCache(token);
        if (data.nombre) {
          setUser({ ...data, name: data.nombre });
        }
      } catch (error) {
        console.error('Error al recargar perfil:', error);
      }
    }
    setCurrentScreen('menu');
  };

  return (
    <>
      {currentScreen === 'login' && (
        <Login onLogin={handleLogin} />
      )}
      
      {showDailySpin && (
        <DailySpin
          onClose={() => {
            setShowDailySpin(false);
            profileCache.timestamp = 0;
            window.dispatchEvent(new Event('profileUpdate'));
          }}
          onPrizeWon={() => { profileCache.timestamp = 0; }}
        />
      )}
      
      {currentScreen !== 'login' && (
        <PoseProvider>
          {currentScreen === 'menu' && (
            <Menu 
              userData={user} 
              onStartTraining={handleStartTraining}
              onStartCombatIA={handleStartCombatIA}
              onStartChallenge={handleStartChallenge}
              onStartMartialTournament={() => setCurrentScreen('martial-tournament')}
              onLogout={handleLogout}
            />
          )}
          
          {currentScreen === 'opponent-selection' && (
            <OpponentSelection
              userData={user}
              onStartCombat={handleStartCombat}
              onBack={handleBackToMenu}
            />
          )}
          
          {currentScreen === 'martial-tournament' && (
            <MartialTournament
              userData={user}
              onStartFight={(index) => {
                if (window._startMartialFight) window._startMartialFight(index);
              }}
              onBack={() => setCurrentScreen('menu')}
            />
          )}
          
          {currentScreen === 'training' && (
            <TrainingMode
              userData={{
                name: user?.name || localStorage.getItem('userName'),
                avatar: localStorage.getItem('userAvatar'),
                genero: user?.genero
              }}
              onBackToMenu={handleBackToMenu}
            />
          )}
          
          {currentScreen === 'game' && (
              <App 
                userData={{
                  name: user?.name || localStorage.getItem('userName'),
                  avatar: localStorage.getItem('userAvatar'),
                  genero: user?.genero
                }}
                opponentData={selectedOpponent}
                challengeId={activeChallengeId}
                onBackToMenu={handleBackToMenu}
              />
          )}
        </PoseProvider>
      )}
    </>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<Root />);
